import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { MotifGeneratorType } from '../components/MotifPatternGenerator/types'
import routePaths from '../constants/routePaths'
import { UIElement } from '../types/UIElements'
import { useScreensaverTimer } from '../utils/screensaverTimer'
import { useContextMotifDetails } from './motifDetailsContext'
import { useContextMotifGenerator } from './motifGeneratorContext'
import { useContextSelectedMotifs } from './selectedMotifContext'
import { useContextUI } from './UIContext'

interface ContextType {
  isScreensaverOn: boolean
  setIsScreensaverOn: Dispatch<SetStateAction<boolean>>
}

interface Props {
  children: ReactNode
}

const Context = createContext({} as ContextType)

const MAX_MOTIF_NUMBER = 4
const NUMBER_OF_MOTIFS = 1004 // The number of motifs + 1

const ScreensaverContext = ({ children }: Props) => {
  const [isScreensaverOn, setIsScreensaverOn] = useState<boolean>(false)
  const [previousLocation, setPreviousLocation] = useState<string>('')
  const [previousGeneratorType, setPreviousGeneratorType] = useState<MotifGeneratorType | null>(
    null,
  )
  const navigate = useNavigate()
  const location = useLocation()
  const { allMotifs } = useContextMotifDetails()
  const { selectedMotifs, selectMotif } = useContextSelectedMotifs()
  const { selectGeneratorType, setFreeLayoutGeneratorState, selectedGeneratorType } =
    useContextMotifGenerator()
  const { elementsOpen, filterOutUiElement } = useContextUI()

  const pickRandomMotif = () => {
    if (allMotifs && selectedMotifs.length < MAX_MOTIF_NUMBER) {
      const randomMotifIndex = Math.floor(Math.random() * NUMBER_OF_MOTIFS)
      const randomMotif = allMotifs[randomMotifIndex]
      if (randomMotif) {
        selectMotif(allMotifs[randomMotifIndex])
      }
    }
  }

  const pickRandomLayout = () => {
    const layouts = ['KALEIDO_SQUARE', 'KALEIDO_PENTA', 'KALEIDO_HEXA', 'KALEIDO_OCTA']
    let rand = Math.floor(Math.random() * layouts.length)
    let randomGeneratorTypeValue = layouts[rand] as MotifGeneratorType
    selectGeneratorType(randomGeneratorTypeValue)
  }

  const startScreensaver = useCallback(() => {
    const isPreviousGeneratorEmpty = previousGeneratorType === null

    if (isPreviousGeneratorEmpty) {
      setPreviousLocation(location.pathname)
      setPreviousGeneratorType(selectedGeneratorType)
    }

    setIsScreensaverOn(true)

    if (elementsOpen.includes(UIElement.ABOUT)) {
      filterOutUiElement(UIElement.ABOUT)
    }

    if (elementsOpen.includes(UIElement.GALLERY)) {
      filterOutUiElement(UIElement.GALLERY)
    }

    navigate(routePaths.GENERATE)

    pickRandomLayout()
  }, [
    selectedMotifs,
    routePaths.GENERATE,
    navigate,
    selectGeneratorType,
    location.pathname,
    selectedGeneratorType,
    setFreeLayoutGeneratorState,
    elementsOpen,
    filterOutUiElement,
  ])

  useEffect(() => {
    if (isScreensaverOn && selectedMotifs.length < MAX_MOTIF_NUMBER) {
      pickRandomMotif()
    }
  }, [isScreensaverOn, selectedMotifs])

  useEffect(() => {
    // Restore when screensaver stops
    if (!isScreensaverOn && previousLocation) {
      navigate(previousLocation)

      // Only restore generator type if we came from the generator page
      if (previousLocation === routePaths.GENERATE && previousGeneratorType) {
        selectGeneratorType(previousGeneratorType)
      }

      setPreviousLocation('')
      setPreviousGeneratorType(null)
    }
  }, [isScreensaverOn])

  useScreensaverTimer({
    callback: startScreensaver,
  })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && !event.altKey && event.code === 'KeyS') {
        event.preventDefault()
        startScreensaver()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [startScreensaver])

  return (
    <Context.Provider value={{ isScreensaverOn, setIsScreensaverOn }}>{children}</Context.Provider>
  )
}

export const useContextScreensaver = () => {
  return useContext(Context)
}

export default ScreensaverContext
